import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Geofence, GeofenceSetting } from '@app-asset-config/models/geofence.model';
import { GeofenceService } from '@app-asset-config/services/geofence.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { GeofenceCreateComponent } from '../geofence-create/geofence-create.component';
import { GeofenceFilterListComponent } from '../geofence-filter-list/geofence-filter-list.component';
import { GeofenceFilterSelectComponent } from '../geofence-filter-select/geofence-filter-select.component';
import { GeofenceBulkImportComponent } from '../geofence-bulk-import/geofence-bulk-import.component';
import { DataService } from '@app-core/services/data/data.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-geofence-listing',
  templateUrl: './geofence-listing.component.html',
  styleUrls: ['./geofence-listing.component.scss'],
})
export class GeofenceListingComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() mode: 'list' | 'multiselect' = 'list';
  @Input() public preSelect: number[] = [];
  @Input() from: 'home' | 'setting' | 'filter' | 'report' = 'home';

  @Output() public selected = new EventEmitter();

  // geofence listing related variables
  public geofenceList: Geofence[] = [];
  public searchGeofence = new FormControl('');

  public unSubscribe = new Subject();

  public preSelected: number[] = [];

  @ViewChild('loadMoreTrigger') loadMoreTrigger!: ElementRef;
  private observer!: IntersectionObserver;

  constructor(public geofenceService: GeofenceService, public dataService: DataService, private translate: TranslateService) {}

  ngOnInit(): void {
    this.preSelected = [...this.preSelect];

    // subscribe to geofence search input
    this.searchGeofence.valueChanges
      .pipe(
        takeUntil(this.unSubscribe),
        debounceTime(300),
        map((val) => val.trim().toLowerCase()),
        distinctUntilChanged()
      )
      .subscribe((val) => {
        this.geofenceService.applyGeofenceFilter({ search: val });
      });

    // subscribe to geofence subject
    this.geofenceService.geofenceSubject.pipe(takeUntil(this.unSubscribe)).subscribe((val) => {
      this.geofenceList =
        val?.map((fence) => {
          const geofence = { ...fence };

          if (!geofence.colourHex) {
            geofence.colourHex = this.geofenceService.DEFAULT_SETTING_COLOR;
          }

          if (geofence.status === 'INACTIVE') {
            geofence.disabled = true;
          }

          if (this.from === 'filter') {
            geofence.disabled = false; // to make the checkbox selectable
          }

          // to disable deselecting already connected geofences (while editing setting)
          if (this.mode === 'multiselect' && this.from === 'setting') {
            if (!geofence.disabled && this.preSelect.includes(geofence.geofenceId)) {
              geofence.disabled = true;
              geofence['disabledReason'] = this.translate.instant('geofenceListingDisabledReason');
              geofence['tooltip'] = this.translate.instant('geofenceListingDisabledTooltip');
            }
          }

          return geofence;
        }) || [];
    });

    // this triggers the initial call for geofence list
    this.geofenceService.applyGeofenceFilter({ search: '', propertyIds: [], tagIds: [] });
  }

  ngAfterViewInit(): void {
    const subscribeForInfiniteScroll = () => {
      if (!this.loadMoreTrigger?.nativeElement) {
        return setTimeout(subscribeForInfiniteScroll, 1000);
      }

      // for infinite scroll
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !this.geofenceService.geofenceSkeletonLoaderSubject.getValue()) {
            this.geofenceService.fetchGeofence();
          }
        },
        { root: null }
      );

      this.observer.observe(this.loadMoreTrigger.nativeElement);
    };

    subscribeForInfiniteScroll();
  }

  ngOnDestroy(): void {
    this.unSubscribe.next();
    this.observer?.disconnect();
  }

  navigateToEditGeofence(geofence) {
    if (this.from !== 'home') return;
    this.geofenceService.navigateTo(GeofenceCreateComponent, { geofence });
  }

  navigateToCreateGeofence() {
    this.geofenceService.commonDataBus.next({ event: 'SHOW_CREATE_GEOFENCE_SEARCH' });
    this.geofenceService.navigateTo(GeofenceCreateComponent);
  }

  navigateToBulkImportGeofence() {
    this.geofenceService.navigateTo(GeofenceBulkImportComponent);
  }

  navigateToFilterList() {
    // getting already applied filter and passing
    let appliedFilter = this.geofenceService.geofenceFilter;
    this.geofenceService.navigateToWithoutHide(GeofenceFilterListComponent, {
      appliedFilter,
      'output:selected': (val) => this.geofenceService.applyGeofenceFilter(val),
    });
  }

  openSettingList() {
    // update the setting to selected geofence
    if (this.mode === 'multiselect' && this.from === 'setting') {
      // find the difference between preSelected and preSelect
      const difference = this.preSelected.filter((id) => !this.preSelect.includes(id));
      this.selected.emit(difference);
      this.geofenceService.pop();
      return;
    }

    this.geofenceService.navigateToWithoutHide(GeofenceFilterSelectComponent, {
      filterType: 'propertyIds:select',
      'output:selected': this.applyTheSettingToSelectedGeofence.bind(this),
    });
  }

  onCheckboxChange(event: any, geofenceId: number) {
    if (event.checked) {
      if (!this.preSelected.includes(geofenceId)) {
        this.preSelected.push(geofenceId);
      }
    } else {
      const index = this.preSelected.indexOf(geofenceId);
      if (index > -1) {
        this.preSelected.splice(index, 1);
      }
    }
  }

  isChecked(geofenceId: number): boolean {
    return this.preSelected.includes(geofenceId);
  }

  unSelectAll() {
    // do not unselect all for this condition
    if (this.mode === 'multiselect' && this.from === 'setting') {
      this.preSelected = [...this.preSelect];
      return;
    }
    this.preSelected = [];
  }

  applyTheSettingToSelectedGeofence(selection: { 'propertyIds:select': GeofenceSetting }) {
    const setting = selection['propertyIds:select'];

    const promises = [];

    // updating all the geofence with selected propertyId
    this.preSelected.forEach((geofenceId) => {
      promises.push(this.geofenceService.updateGeofence(geofenceId, { propertyId: setting.propertyId }).toPromise());
    });

    Promise.all(promises)
      .then((data) => {
        data.length;
        this.geofenceService.showToasts(this.translate.instant('geofenceListingSettingUpdateSuccess'));
        this.geofenceService.fetchGeofence(true);

        if (this.from == 'setting') {
          this.selected.emit(this.preSelected);
          this.geofenceService.pop();
        }
        this.unSelectAll();
      })
      .catch((err) => {
        console.log(err);
        this.geofenceService.showToasts('Geofence updates failed');
      });
  }

  updateSelection() {
    this.selected.emit(this.preSelected);
    this.geofenceService.pop();
  }

  goBack() {
    this.geofenceService.pop();
  }
}
